/**
 * Auth state management with Zustand.
 */
import { create } from "zustand";
import api from "../services/api";
import { setSentryUser, clearSentryUser } from "../observability/sentry";

const useAuthStore = create((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,

  login: async (email, password) => {
    const response = await api.post("/auth/login", { email, password });
    const { access_token, refresh_token, user } = response.data;

    localStorage.setItem("access_token", access_token);
    localStorage.setItem("refresh_token", refresh_token);

    // Attach the user to the Sentry scope so any error captured
    // from this point forward carries tenant context (org_id, role).
    // No-op when Sentry isn't configured.
    setSentryUser(user);

    set({ user, isAuthenticated: true });
    return user;
  },

  logout: async () => {
    // Phase 42 audit (F2): pre-fix this only cleared local state and
    // never told the backend the session was over. The backend's
    // revoked_tokens table (Phase H4 from the original audit) exists
    // for exactly this case — without the API call, the access token
    // remained valid for up to ~15 min after "logout" and could be
    // replayed by anyone who had captured it (XSS, network sniff
    // before the user logged out, shared device).
    //
    // Best-effort POST: we await briefly but swallow any failure so
    // the user always lands on /login even if the network is down or
    // the server is unreachable. The local clear must always happen.
    try {
      await api.post("/auth/logout");
    } catch {
      // Server unreachable / token already expired / etc. — local
      // clear below still proceeds. Log nothing; this is normal on
      // network failure and we don't want a console error spamming
      // every offline logout.
    }

    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    // Clear the Sentry scope before navigating to /login. Without
    // this, errors on the login page after logout would still be
    // attributed to the prior user.
    clearSentryUser();
    set({ user: null, isAuthenticated: false });
  },

  checkAuth: async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        set({ isLoading: false });
        return;
      }

      const response = await api.get("/auth/me");
      // Restore the Sentry scope on session resume (refresh, new tab).
      setSentryUser(response.data.user);
      set({ user: response.data.user, isAuthenticated: true, isLoading: false });
    } catch {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      clearSentryUser();
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },

  hasRole: (...roles) => {
    const state = useAuthStore.getState();
    return state.user && roles.includes(state.user.role);
  },
}));

export default useAuthStore;
